"use client";

import { cn } from "@newxsystems/shared";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  Icon,
} from "@templates/design-system";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuIndicator,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@templates/design-system/navigation-menu";
import {
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { resolveTootPrimitive } from "../../../../client";
import type {
  ITootMegaMenuProps,
  TootMegaMenuAction,
  TootMegaMenuItem,
  TootMegaMenuLink,
  TootMegaMenuLinkItem,
} from "./TootMegaMenu.props";

const isExternalTarget = (target?: string) => target === "_blank";

const linkRel = (target?: string) => (isExternalTarget(target) ? "noopener noreferrer" : undefined);

const toComparablePath = (href: string) => {
  const path = href.startsWith("http") ? new URL(href).pathname : href.split(/[?#]/)[0];

  return path.length > 1 ? path.replace(/\/$/, "") : path;
};

const isActiveHref = (href: string, currentPath?: string) => {
  if (
    !currentPath ||
    href.startsWith("http") ||
    href.startsWith("mailto:") ||
    href.startsWith("tel:")
  ) {
    return false;
  }

  return toComparablePath(href) === toComparablePath(currentPath);
};

const itemContainsActiveLink = (item: TootMegaMenuItem, currentPath?: string) => {
  if (item.blockType === "megaMenuLink") {
    return isActiveHref(item.href, currentPath);
  }

  if (item.blockType === "megaMenuDropdown") {
    return item.links.some((link) => isActiveHref(link.href, currentPath));
  }

  return (item.groups ?? []).some((group) =>
    (group.links ?? []).some((link) => isActiveHref(link.href, currentPath)),
  );
};

const ChevronIcon = () => (
  <Icon
    aria-hidden="true"
    className="size-3.5 shrink-0 transition-transform duration-200 ease-out group-open:rotate-180 motion-reduce:transition-none"
    name="chevron-down"
  />
);

const ArrowIcon = () => (
  <Icon aria-hidden="true" className="size-3.5 shrink-0" name="arrow-up-right" />
);

const desktopTriggerClassName =
  "min-h-16 rounded-none border-0 border-r border-[color-mix(in_oklab,var(--color-toot-grid-strong)_70%,transparent)] bg-transparent px-3 text-xs font-black uppercase tracking-[0.18em] text-[color-mix(in_oklab,var(--color-toot-foreground)_78%,transparent)] hover:bg-[color-mix(in_oklab,var(--color-toot-red)_10%,transparent)] hover:text-[var(--color-toot-foreground)] focus-visible:ring-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--color-ring)] data-[state=open]:bg-[color-mix(in_oklab,var(--color-toot-red)_14%,transparent)] data-[state=open]:text-[var(--color-toot-foreground)] data-[state=open]:shadow-[inset_0_-2px_0_var(--color-toot-red)] lg:px-4";

const MenuLink = ({
  className,
  currentPath,
  link,
}: {
  className?: string;
  currentPath?: string | undefined;
  link: TootMegaMenuLink;
}) => {
  const Link = resolveTootPrimitive("Link");
  const active = isActiveHref(link.href, currentPath);

  return (
    <Link
      aria-label={link.ariaLabel}
      aria-current={active ? "page" : undefined}
      className={cn(
        "group/link flex min-h-12 items-center justify-between gap-4 rounded-[var(--radius-toot-panel)] border border-transparent bg-toot-surface-raised/50 px-3.5 py-3 text-sm text-[color-mix(in_oklab,var(--color-toot-foreground)_86%,transparent)] transition-[background-color,border-color,color,transform] duration-200 ease-out hover:translate-x-0.5 hover:border-[color-mix(in_oklab,var(--color-toot-red)_50%,transparent)] hover:bg-[color-mix(in_oklab,var(--color-toot-red)_10%,transparent)] hover:text-[var(--color-toot-foreground)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-ring)] motion-reduce:transition-none motion-reduce:hover:translate-x-0",
        active &&
          "border-[color-mix(in_oklab,var(--color-toot-red)_64%,transparent)] bg-[color-mix(in_oklab,var(--color-toot-red)_14%,transparent)] text-[var(--color-toot-foreground)]",
        className,
      )}
      href={link.href}
      rel={linkRel(link.target)}
      target={link.target}
    >
      <span className="min-w-0 [overflow-wrap:anywhere]">
        <span className="block text-xs font-black uppercase tracking-[0.16em]">{link.text}</span>
      </span>
      <span className="text-[var(--color-toot-orange)] opacity-0 transition duration-200 ease-out group-hover/link:opacity-100 group-focus-visible/link:opacity-100 motion-reduce:transition-none">
        <ArrowIcon />
      </span>
    </Link>
  );
};

const FeaturedMenuLink = ({
  currentPath,
  link,
}: {
  currentPath?: string | undefined;
  link: TootMegaMenuLink;
}) => {
  const Link = resolveTootPrimitive("Link");
  const active = isActiveHref(link.href, currentPath);

  return (
    <Link
      aria-label={link.ariaLabel}
      aria-current={active ? "page" : undefined}
      className={cn(
        "group/feature relative isolate grid min-h-16 overflow-hidden rounded-[var(--radius-toot-panel)] border border-[color-mix(in_oklab,var(--color-toot-grid-strong)_68%,transparent)] bg-[radial-gradient(circle_at_100%_0%,rgba(255,77,46,0.1),transparent_12rem),linear-gradient(180deg,var(--color-toot-surface-raised),var(--color-toot-black))] p-3 text-[var(--color-toot-foreground)] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] transition-[border-color,background-color,transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:border-[color-mix(in_oklab,var(--color-toot-orange)_60%,transparent)] hover:shadow-[0_1rem_2.4rem_rgba(0,0,0,0.36),0_0_0_1px_rgba(255,77,46,0.12)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-ring)] motion-reduce:transition-none motion-reduce:hover:translate-y-0",
        active &&
          "border-[color-mix(in_oklab,var(--color-toot-red)_70%,transparent)] bg-[color-mix(in_oklab,var(--color-toot-red)_12%,var(--color-toot-surface-terminal))]",
      )}
      href={link.href}
      rel={linkRel(link.target)}
      target={link.target}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,77,46,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,138,31,0.05)_1px,transparent_1px)] bg-[size:1.4rem_1.4rem] opacity-45 [mask-image:linear-gradient(180deg,black,transparent_88%)]"
      />
      <span className="relative z-10 text-balance text-sm font-black leading-5 tracking-[-0.035em] text-[color-mix(in_oklab,var(--color-toot-foreground)_95%,transparent)]">
        {link.text}
      </span>
    </Link>
  );
};

const DesktopPanelShell = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "toot-heat-panel relative isolate overflow-hidden rounded-[var(--radius-toot-frame)] p-3 text-[var(--color-toot-foreground)] shadow-[0_1.5rem_4rem_rgba(0,0,0,0.58),0_0_0_1px_rgba(255,77,46,0.12)]",
      className,
    )}
  >
    <div className="relative z-10">{children}</div>
  </div>
);

const DesktopLinkNavItem = ({
  currentPath,
  item,
}: {
  currentPath?: string | undefined;
  item: TootMegaMenuLinkItem;
}) => {
  const Link = resolveTootPrimitive("Link");
  const active = isActiveHref(item.href, currentPath);

  return (
    <NavigationMenuItem>
      <NavigationMenuLink asChild>
        <Link
          aria-label={item.ariaLabel}
          aria-current={active ? "page" : undefined}
          className={cn(
            desktopTriggerClassName,
            "inline-flex items-center justify-center",
            active &&
              "bg-[color-mix(in_oklab,var(--color-toot-red)_14%,transparent)] text-[var(--color-toot-foreground)] shadow-[inset_0_-2px_0_var(--color-toot-red)]",
          )}
          href={item.href}
          rel={linkRel(item.target)}
          target={item.target}
        >
          <span className="truncate">{item.text}</span>
        </Link>
      </NavigationMenuLink>
    </NavigationMenuItem>
  );
};

const DesktopDropdownNavItem = ({
  currentPath,
  item,
}: {
  currentPath?: string | undefined;
  item: Extract<TootMegaMenuItem, { blockType: "megaMenuDropdown" }>;
}) => (
  <NavigationMenuItem>
    <NavigationMenuTrigger
      className={cn(
        desktopTriggerClassName,
        itemContainsActiveLink(item, currentPath) &&
          "bg-[color-mix(in_oklab,var(--color-toot-red)_14%,transparent)] text-[var(--color-toot-foreground)] shadow-[inset_0_-2px_0_var(--color-toot-red)]",
      )}
    >
      <span className="truncate">{item.label}</span>
    </NavigationMenuTrigger>
    <NavigationMenuContent>
      <DesktopPanelShell className="w-[min(92vw,42rem)]">
        {(item.links ?? []).length ? (
          <div className="grid gap-3 md:grid-cols-2">
            {item.links.map((link, index) => (
              <FeaturedMenuLink
                currentPath={currentPath}
                key={`${link.href}-${link.text}-${index}`}
                link={link}
              />
            ))}
          </div>
        ) : null}
      </DesktopPanelShell>
    </NavigationMenuContent>
  </NavigationMenuItem>
);

const DesktopGroupNavItem = ({
  currentPath,
  item,
}: {
  currentPath?: string | undefined;
  item: Extract<TootMegaMenuItem, { blockType: "megaMenuGroup" }>;
}) => (
  <NavigationMenuItem>
    <NavigationMenuTrigger
      className={cn(
        desktopTriggerClassName,
        itemContainsActiveLink(item, currentPath) &&
          "bg-[color-mix(in_oklab,var(--color-toot-red)_14%,transparent)] text-[var(--color-toot-foreground)] shadow-[inset_0_-2px_0_var(--color-toot-red)]",
      )}
    >
      <span className="truncate">{item.label}</span>
    </NavigationMenuTrigger>
    <NavigationMenuContent>
      <DesktopPanelShell className="w-[min(92vw,48rem)]">
        {(item.groups ?? []).length ? (
          <div className="grid gap-3 md:grid-cols-2">
            {(item.groups ?? []).map((group, groupIndex) => (
              <section
                className="rounded-[var(--radius-toot-panel)] border border-[color-mix(in_oklab,var(--color-toot-orange)_26%,transparent)] bg-toot-surface-raised/80 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]"
                key={`${group.header}-${groupIndex}`}
              >
                <h3 className="text-xs font-black uppercase tracking-[0.18em] text-[var(--color-toot-orange)]">
                  {group.header}
                </h3>
                <div className="mt-3 grid gap-1.5">
                  {group.links.map((link, linkIndex) => (
                    <MenuLink
                      currentPath={currentPath}
                      key={`${link.href}-${link.text}-${linkIndex}`}
                      link={link}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : null}
      </DesktopPanelShell>
    </NavigationMenuContent>
  </NavigationMenuItem>
);

const DesktopNavigation = ({
  currentPath,
  items,
}: {
  currentPath?: string | undefined;
  items: TootMegaMenuItem[];
}) => {
  if (!items.length) {
    return null;
  }

  return (
    <NavigationMenu
      aria-label="Primary navigation"
      className="hidden min-w-0 flex-1 justify-start border-l border-[color-mix(in_oklab,var(--color-toot-grid-strong)_70%,transparent)] md:flex"
    >
      <NavigationMenuList className="h-full justify-start gap-0 rounded-none bg-transparent p-0">
        {items.map((item, index) => {
          if (item.blockType === "megaMenuLink") {
            return (
              <DesktopLinkNavItem
                currentPath={currentPath}
                item={item}
                key={`${item.href}-${item.text}-${index}`}
              />
            );
          }

          if (item.blockType === "megaMenuDropdown") {
            return (
              <DesktopDropdownNavItem
                currentPath={currentPath}
                item={item}
                key={`${item.label}-${index}`}
              />
            );
          }

          return (
            <DesktopGroupNavItem
              currentPath={currentPath}
              item={item}
              key={`${item.label}-${index}`}
            />
          );
        })}
      </NavigationMenuList>
      <NavigationMenuIndicator className="[&>span]:border-[color-mix(in_oklab,var(--color-toot-grid-strong)_78%,transparent)] [&>span]:bg-[var(--color-toot-surface-terminal)]" />
    </NavigationMenu>
  );
};

const ActionLink = ({
  action,
  currentPath,
}: {
  action: TootMegaMenuAction;
  currentPath?: string | undefined;
}) => {
  const Link = resolveTootPrimitive("Link");
  const isPrimary = action.style === "primary";
  const active = isActiveHref(action.href, currentPath);

  return (
    <Link
      aria-label={action.ariaLabel}
      aria-current={active ? "page" : undefined}
      className={cn(
        "toot-button min-h-11 whitespace-nowrap px-4 text-[0.72rem] font-black uppercase tracking-[0.14em] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-ring)]",
        isPrimary
          ? "toot-button-primary shadow-[0_0_1.4rem_rgba(255,77,46,0.2)] hover:shadow-[0_0_1.8rem_rgba(255,77,46,0.28)]"
          : "toot-button-outline bg-[color-mix(in_oklab,var(--color-toot-orange)_8%,transparent)] hover:bg-[color-mix(in_oklab,var(--color-toot-orange)_14%,transparent)]",
        active &&
          "border-[var(--color-toot-orange)] bg-[var(--color-toot-red)] text-[var(--color-primary-foreground)]",
      )}
      href={action.href}
      rel={linkRel(action.target)}
      target={action.target}
    >
      {action.text}
    </Link>
  );
};

export const TootMegaMenu = ({
  actions: actionItems,
  blockName,
  className,
  id,
  items: menuItems,
  label = "Primary navigation",
  logo,
  mobileLabel = "Menu",
}: ITootMegaMenuProps) => {
  const Image = resolveTootPrimitive("Image");
  const Link = resolveTootPrimitive("Link");
  const actions = actionItems ?? [];
  const items = menuItems ?? [];
  const navRef = useRef<HTMLElement>(null);
  const uid = useId();
  const [currentPath, setCurrentPath] = useState<string>();
  const [mobileOpen, setMobileOpen] = useState(false);
  const logoLabel = logo?.caption;
  const logoStyle: CSSProperties | undefined = logo?.focalPoint
    ? { objectPosition: `${logo.focalPoint.x * 100}% ${logo.focalPoint.y * 100}%` }
    : undefined;

  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, []);

  if (!logo && !items.length && !actions.length) {
    return null;
  }

  const hasMenuContent = items.length > 0 || actions.length > 0;
  const logoShellClassName =
    "group/logo flex min-w-0 items-center gap-3 px-3 py-2 text-left transition-colors duration-200 ease-out hover:bg-[color-mix(in_oklab,var(--color-toot-orange)_6%,transparent)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--color-ring)]";
  const logoContent = logo ? (
    <>
      <span className="relative grid size-11 shrink-0 place-items-center overflow-hidden rounded-[var(--radius-toot-panel)] border border-toot-grid bg-card p-1.5 shadow-sm lg:size-12">
        <Image
          alt={logo.alternateText}
          aspectRatio="infographic"
          className="relative z-10 h-full w-full"
          decoding="async"
          imageClassName="h-full w-full !object-contain opacity-95"
          height={logo.height ?? undefined}
          loading="eager"
          sizes={logo.sizes ?? undefined}
          src={logo.defaultSrc}
          srcSet={logo.srcSet ?? undefined}
          style={logoStyle}
          width={logo.width ?? undefined}
        />
      </span>
      {logoLabel ? (
        <span className="min-w-0 truncate text-sm font-black uppercase leading-5 tracking-[0.18em] text-[color-mix(in_oklab,var(--color-toot-foreground)_92%,transparent)]">
          {logoLabel}
        </span>
      ) : null}
    </>
  ) : null;

  const onNavKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape") {
      navRef.current?.querySelectorAll("details[open]").forEach((details) => {
        details.removeAttribute("open");
      });
      setMobileOpen(false);
      return;
    }

    if (!items.length || (event.key !== "ArrowRight" && event.key !== "ArrowLeft")) {
      return;
    }

    event.preventDefault();
    const focusable = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), summary[tabindex="0"], summary',
      ),
    ).filter((element) => element.offsetParent !== null);
    if (!focusable.length) {
      return;
    }

    const currentIndex = focusable.indexOf(document.activeElement as HTMLElement);
    const direction = event.key === "ArrowRight" ? 1 : -1;
    const nextElement =
      focusable[(Math.max(0, currentIndex) + direction + focusable.length) % focusable.length];
    nextElement?.focus();
  };

  return (
    <nav
      aria-label={label}
      data-block-name={blockName}
      id={id}
      ref={navRef}
      className={cn(
        "relative z-50 isolate overflow-visible rounded-[var(--toot-radius-card)] border border-[color-mix(in_oklab,var(--color-toot-orange)_34%,var(--color-toot-grid-strong))] bg-[radial-gradient(circle_at_0%_0%,color-mix(in_oklab,var(--color-toot-red)_18%,transparent),transparent_18rem),linear-gradient(180deg,var(--color-toot-surface-terminal),var(--color-toot-black)_82%)] text-[var(--color-toot-foreground)] shadow-[0_1.25rem_3.5rem_rgba(0,0,0,0.44),inset_0_1px_0_rgba(255,255,255,0.07)]",
        className,
      )}
      onKeyDown={onNavKeyDown}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-[inherit] bg-[linear-gradient(rgba(255,77,46,0.07)_1px,transparent_1px),linear-gradient(90deg,rgba(255,138,31,0.045)_1px,transparent_1px)] bg-[size:1.75rem_1.75rem] opacity-34 [mask-image:linear-gradient(180deg,black,transparent_82%)]"
      />

      <div className="relative z-10 grid rounded-[var(--toot-radius-card)] min-h-16 grid-cols-[1fr_auto] items-stretch border-b border-[color-mix(in_oklab,var(--color-toot-grid-strong)_72%,transparent)] bg-toot-surface-raised/70 md:grid-cols-[auto_minmax(0,1fr)_auto]">
        {logo ? (
          <Link aria-label="Go to home page" className={logoShellClassName} href="/">
            {logoContent}
          </Link>
        ) : null}

        <DesktopNavigation currentPath={currentPath} items={items} />

        <div className="flex items-center justify-end gap-2 border-l border-[color-mix(in_oklab,var(--color-toot-grid-strong)_60%,transparent)] px-3 py-3 sm:px-4">
          {actions.length ? (
            <div className="hidden flex-wrap items-center justify-end gap-2 md:flex">
              {actions.map((action, index) => (
                <ActionLink
                  action={action}
                  currentPath={currentPath}
                  key={`${action.href}-${action.text}-${index}`}
                />
              ))}
            </div>
          ) : null}
          {hasMenuContent ? (
            <button
              aria-controls={`${uid}-mobile`}
              aria-expanded={mobileOpen}
              className="inline-flex min-h-11 items-center justify-center gap-3 rounded-[var(--radius-toot-panel)] border border-[color-mix(in_oklab,var(--color-toot-orange)_52%,transparent)] bg-[color-mix(in_oklab,var(--color-toot-orange)_8%,transparent)] px-4 text-xs font-black uppercase tracking-[0.18em] text-[color-mix(in_oklab,var(--color-toot-orange)_86%,var(--color-toot-foreground))] transition-[background-color,border-color,color] duration-200 ease-out hover:border-[var(--color-toot-orange)] hover:bg-[color-mix(in_oklab,var(--color-toot-orange)_14%,transparent)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-ring)] motion-reduce:transition-none md:hidden"
              type="button"
              onClick={() => setMobileOpen((open) => !open)}
            >
              <span>{mobileLabel}</span>
            </button>
          ) : null}
        </div>
      </div>

      {hasMenuContent ? (
        <Drawer direction="right" open={mobileOpen} onOpenChange={setMobileOpen}>
          <DrawerContent
            direction="right"
            className="h-dvh w-[min(28rem,100vw)] max-w-none gap-0 border-[color-mix(in_oklab,var(--color-toot-orange)_34%,var(--color-toot-grid-strong))] bg-[var(--color-toot-black)] p-0 text-[var(--color-toot-foreground)] shadow-[0_1.25rem_3.5rem_rgba(0,0,0,0.56)] md:hidden"
            id={`${uid}-mobile`}
          >
            <DrawerHeader className="shrink-0 border-b border-[color-mix(in_oklab,var(--color-toot-grid-strong)_58%,transparent)] bg-toot-surface-raised/75 p-4 text-left">
              <DrawerTitle className="text-sm font-black uppercase tracking-[0.18em] text-[var(--color-toot-orange)]">
                Navigation menu
              </DrawerTitle>
              <DrawerDescription className="text-sm text-[color-mix(in_oklab,var(--color-toot-foreground)_72%,transparent)]">
                Browse site links and actions.
              </DrawerDescription>
            </DrawerHeader>
            <div className="min-h-0 flex-1 overflow-y-auto bg-toot-surface-raised/75 p-3">
              <div className="grid gap-3">
                {items.map((item, index) =>
                  item.blockType === "megaMenuLink" ? (
                    <MenuLink
                      className="border-[color-mix(in_oklab,var(--color-toot-grid-strong)_58%,transparent)] bg-toot-surface-raised/70"
                      currentPath={currentPath}
                      key={`${item.href}-${item.text}-${index}`}
                      link={item}
                    />
                  ) : (
                    <details
                      className="group overflow-hidden rounded-[var(--radius-toot-panel)] border border-[color-mix(in_oklab,var(--color-toot-grid-strong)_64%,transparent)] bg-toot-surface-raised/75"
                      key={`${item.label}-${index}`}
                    >
                      <summary
                        className={cn(
                          "flex min-h-12 cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-[var(--color-toot-orange)] transition-[background-color,color] duration-200 ease-out marker:hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--color-ring)] group-open:bg-[color-mix(in_oklab,var(--color-toot-red)_14%,transparent)] group-open:text-[var(--color-toot-foreground)] motion-reduce:transition-none [&::-webkit-details-marker]:hidden",
                          itemContainsActiveLink(item, currentPath) &&
                            "bg-[color-mix(in_oklab,var(--color-toot-red)_14%,transparent)] text-[var(--color-toot-foreground)]",
                        )}
                      >
                        <span>{item.label}</span>
                        <ChevronIcon />
                      </summary>
                      <div className="grid max-h-[min(52dvh,28rem)] gap-2 overflow-y-auto overscroll-contain border-t border-[color-mix(in_oklab,var(--color-toot-grid-strong)_58%,transparent)] p-3 [scrollbar-gutter:stable]">
                        {item.blockType === "megaMenuDropdown"
                          ? item.links.map((link, linkIndex) => (
                              <MenuLink
                                currentPath={currentPath}
                                key={`${link.href}-${link.text}-${linkIndex}`}
                                link={link}
                              />
                            ))
                          : (item.groups ?? []).map((group, groupIndex) => (
                              <section className="grid gap-2" key={`${group.header}-${groupIndex}`}>
                                <h3 className="px-3 text-[0.66rem] font-black uppercase tracking-[0.18em] text-[color-mix(in_oklab,var(--color-toot-muted)_84%,var(--color-toot-foreground))]">
                                  {group.header}
                                </h3>
                                {group.links.map((link, linkIndex) => (
                                  <MenuLink
                                    currentPath={currentPath}
                                    key={`${link.href}-${link.text}-${linkIndex}`}
                                    link={link}
                                  />
                                ))}
                              </section>
                            ))}
                      </div>
                    </details>
                  ),
                )}
              </div>
            </div>
            {actions.length ? (
              <DrawerFooter className="shrink-0 border-t border-[color-mix(in_oklab,var(--color-toot-grid-strong)_58%,transparent)] bg-[var(--color-toot-black)]/95 p-3 shadow-[0_-18px_42px_rgba(0,0,0,0.32)] backdrop-blur-md">
                <div className="grid gap-2">
                  {actions.map((action, index) => (
                    <ActionLink
                      action={action}
                      currentPath={currentPath}
                      key={`${action.href}-${action.text}-${index}`}
                    />
                  ))}
                </div>
              </DrawerFooter>
            ) : null}
          </DrawerContent>
        </Drawer>
      ) : null}
    </nav>
  );
};

TootMegaMenu.displayName = "Interactive::TootMegaMenu";
